import { Prisma } from "@prisma/client";
import { BuyableItemEvent, BuyableItemHandler } from "../buyable-item.types";

type PartialExhibitorPromotionBuyableItem = {
  exhibitorPromotionEvent?: { id: string } | null;
};

type ExhibitorPromotionBuyableItem = {
  exhibitorPromotionEvent: { id: string };
};

type ExhibitorPromotionWithEvent = {
  exhibitorPromotionEvent: BuyableItemEvent;
};

const GROUP_KEY_PREFIX = "exhibitorPromotion";

export const ExhibitorPromotionBuyableItemHandler = {
  displayName: "Mise en avant exposant",
  isCumulative: true,
  accounting: {
    generalAccountCode: "706508",
    invoiceLabel: "Mise en avant exposant",
  },

  getEvent(item: ExhibitorPromotionWithEvent): BuyableItemEvent {
    return item.exhibitorPromotionEvent;
  },

  isValid(item: PartialExhibitorPromotionBuyableItem): boolean {
    return !!item.exhibitorPromotionEvent?.id;
  },

  findEquivalent(item: PartialExhibitorPromotionBuyableItem): Prisma.BuyableItemWhereInput {
    return {
      exhibitorPromotionEvent: {
        id: item.exhibitorPromotionEvent?.id,
      },
    };
  },

  create({
    exhibitorPromotionEvent,
  }: ExhibitorPromotionBuyableItem): Omit<Prisma.BuyableItemCreateInput, "buyableType"> {
    return {
      exhibitorPromotionEvent: {
        connect: {
          id: exhibitorPromotionEvent.id,
        },
      },
    };
  },

  parseGroupKeyAsWhere(groupKey: string): Prisma.BuyableItemWhereInput {
    if (!ExhibitorPromotionBuyableItemHandler.isGroupKey(groupKey)) {
      throw new Error(`Invalid group key for exhibitor promotion: ${groupKey}`);
    }

    const [, eventId] = groupKey.split("|");
    return {
      exhibitorPromotionEvent: {
        id: eventId,
      },
    };
  },

  isGroupKey(groupKey: string): boolean {
    return groupKey.startsWith(`${GROUP_KEY_PREFIX}|`);
  },

  getGroupKey(item: PartialExhibitorPromotionBuyableItem): string {
    const eventId = item.exhibitorPromotionEvent?.id;
    if (!eventId) {
      throw new Error("Cannot get group key for invalid exhibitorPromotion item");
    }
    return `${GROUP_KEY_PREFIX}|${eventId}`;
  },

  getPricingQuery(): Prisma.PricingFindFirstArgs {
    return {
      where: {
        backofficePrestation: {
          type: "exhibitorPromotion",
        },
      },
    };
  },

  getEventSearchFilter(searchText: string): Prisma.BuyableItemWhereInput {
    return {
      exhibitorPromotionEvent: {
        name: { contains: searchText, mode: "insensitive" },
      },
    };
  },
} as const satisfies BuyableItemHandler;
